import React, { useState, useMemo } from 'react';
import { formatCurrencySigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import ExVesselValueModal from '../ExVesselValueModal/ExVesselValueModal';
import InsightColumnPair from '../InsightColumnPair';
import './MissedRevenueToFishermen.css';

function filterExvesselByYear(
  exvessel: unknown[] | undefined,
  selectedYear: string
): unknown[] {
  if (!exvessel || !Array.isArray(exvessel)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return exvessel.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return exvessel.filter((item) => withYear(item) >= 2025);
  return exvessel;
}

interface InsightData {
  missedValue?: number;
  missedValueUncertainty?: number;
}

interface MissedRevenueToFishermenData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  onOpenExVesselModal?: () => void;
}

const DESCRIPTION =
  'The economic value of the fish that were not caught. <i>Missed weight</i> × <i>ex-vessel price per unit weight</i>';

export default function MissedRevenueToFishermen(
  props: RegistryComponentProps
): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<MissedRevenueToFishermenData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { exvessel?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredExvessel = useMemo(
    () => filterExvesselByYear(pageTimeSeriesData?.exvessel, selectedYear),
    [pageTimeSeriesData?.exvessel, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenExVesselModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;

  return (
    <div className="missed-revenue-to-fishermen insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Missed Revenue to Fishermen</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: DESCRIPTION }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.missedValue}
        currentValue={insights.missedValue}
        statusQuoContent={
          <>
            {formatCurrencySigFig(statusQuoDisplay.missedValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(statusQuoDisplay.missedValueUncertainty, false)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatCurrencySigFig(insights.missedValue)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(insights.missedValueUncertainty, false)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenExVesselModal}
        clickable={Boolean(onOpenExVesselModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      <ExVesselValueModal
        isOpen={showModal}
        onClose={handleCloseModal}
        data={filteredExvessel}
        loading={pageLoading}
        error={pageError}
        stockName={pageStockName}
      />
    </div>
  );
}
